import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  useLocation,
  useMatches,
  useParams,
} from "react-router";
import { appModules } from "../modules";
import type { LayoutKey, RouteHandle } from "../modules/types";
import { ErrorPage } from "../pages/ErrorPage";
import { NotFoundPage } from "../pages/NotFoundPage";
import { PrivacyPage } from "../pages/PrivacyPage";
import { TermsPage } from "../pages/TermsPage";
import { ToastProvider } from "../ui/Toast";
import { AuthGate, GuestOnly } from "./AuthGate";
import { AppShell } from "./layouts/AppShell";
import { FullPageSettingsLayout } from "./layouts/FullPageSettingsLayout";
import {
  HandoffLayout,
  KioskLayout,
  KioskPending,
  SharedReportLayout,
  SharedReportPending,
  StaticLayout,
} from "./layouts/OtherLayouts";
import { PublicLayout } from "./layouts/PublicLayout";
import { entryPathname, mergeSidebar } from "./nav";
import { PendingPage } from "./PendingPage";
import { useLogoutBroadcast, useMe, useThemeSync } from "./session";
import { useRouteTitle } from "./title";

function Root() {
  useRouteTitle();
  useThemeSync();
  useLogoutBroadcast();
  return (
    <ToastProvider>
      <Outlet />
    </ToastProvider>
  );
}

function routesFor(layout: LayoutKey): RouteObject[] {
  return appModules.flatMap((m) => m.routes[layout] ?? []);
}

/** Auth card routes are guest-only unless the deepest route opts out (`handle.guestOnly === false`). */
function AuthLayout() {
  const matches = useMatches();
  const last = matches[matches.length - 1];
  const guestOnly = (last?.handle as RouteHandle | undefined)?.guestOnly !== false;
  return guestOnly ? (
    <GuestOnly>
      <PublicLayout />
    </GuestOnly>
  ) : (
    <PublicLayout />
  );
}

// Page titles per routes.md 2.3 where they differ from the sidebar label.
const titleOverrides: Partial<Record<string, string>> = {
  REPORTS: "Reports",
  KIOSK: "Kiosks",
  ACTIVITY: "Activity",
  ACTIVITY_MONITORING: "Activity-monitoring",
};

/** Every sidebar destination renders something, even before its feature module is merged. */
function pendingShellRoutes(): RouteObject[] {
  const registered = new Set(routesFor("shell").map((r) => r.path));
  const out: RouteObject[] = [];
  const add = (path: string, title: string, name: string) => {
    if (registered.has(path) || out.some((r) => r.path === path)) return;
    out.push({ path, handle: { title }, element: <PendingPage name={name} /> });
  };
  for (const e of mergeSidebar(appModules)) {
    const title = titleOverrides[e.id] ?? e.label;
    add(entryPathname(e.path), title, e.label);
    for (const g of e.flyout ?? []) for (const it of g.items) add(it.path, title, e.label);
  }
  add("/user/settings", "User", "My profile");
  add("/user/preferences", "User", "Preferences");
  add("/workspaces", "Workspaces", "Manage workspaces");
  return out;
}

/** Client-side redirects keep query and hash (routes.md 1.9). */
function Redirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  const [path, ownHash] = to.split("#");
  return <Navigate replace to={`${path}${search}${ownHash ? `#${ownHash}` : hash}`} />;
}

const sectionAliases: Partial<Record<string, string>> = {
  "import-export": "import",
  integrations: "integrations-addons",
};

function WorkspaceSettingsRedirect() {
  const me = useMe();
  const { section } = useParams();
  const hash = section ? (sectionAliases[section] ?? section) : "general";
  return <Navigate replace to={`/workspaces/${me.activeWorkspaceId ?? ""}/settings#${hash}`} />;
}

function SettingsSubpathRedirect() {
  const { workspaceId = "", section = "general" } = useParams();
  return <Navigate replace to={`/workspaces/${workspaceId}/settings#${sectionAliases[section] ?? section}`} />;
}

const promoTargets: Partial<Record<string, string>> = {
  approvals: "/approvals#pending",
  expenses: "/expenses",
  invoices: "/invoices",
  scheduling: "/scheduling#projects",
  "time-off": "/time-off#requests",
  activity: "/activity/screenshots",
};

function PromoRedirect() {
  const { feature = "" } = useParams();
  return <Navigate replace to={promoTargets[feature] ?? "/"} />;
}

const shellRedirects: RouteObject[] = [
  { index: true, element: <Navigate replace to="/tracker" /> },
  { path: "/reports", element: <Redirect to="/reports/summary" /> },
  { path: "/reports/attendance", element: <Redirect to="/reports/team/attendance" /> },
  { path: "/reports/assignments", element: <Redirect to="/reports/team/assignments" /> },
  { path: "/reports/expenses", element: <Redirect to="/reports/expense/detailed" /> },
  { path: "/audit-log", element: <Redirect to="/activity/audit-log" /> },
  { path: "/promo/:feature", element: <PromoRedirect /> },
  { path: "/workspace-settings", element: <WorkspaceSettingsRedirect /> },
  { path: "/workspace-settings/:section", element: <WorkspaceSettingsRedirect /> },
  { path: "/settings/workspace", element: <WorkspaceSettingsRedirect /> },
  { path: "/settings/workspace/:section", element: <WorkspaceSettingsRedirect /> },
  { path: "/workspaces/:workspaceId/settings/:section", element: <SettingsSubpathRedirect /> },
  { path: "/user/delete-account", element: <Redirect to="/user/settings/delete" /> },
  { path: "/legacy/workspaces", element: <Redirect to="/workspaces" /> },
  // Billing does not exist in the clone; everything is free.
  ...["/subscription", "/upgrade", "/upgrade/:workspaceId", "/payment", "/payment/legacy"].map(
    (path): RouteObject => ({ path, element: <Navigate replace to="/tracker" /> }),
  ),
];

const devRoutes: RouteObject[] = import.meta.env.DEV
  ? ["/dev/ui-kit", "/__ui"].map(
      (path): RouteObject => ({
        path,
        handle: { title: "UI kit" },
        lazy: () => import("../dev/UiKitPage").then((m) => ({ Component: m.UiKitPage })),
      }),
    )
  : [];

export const routes: RouteObject[] = [
  {
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      { element: <AuthLayout />, children: routesFor("auth") },
      {
        element: <StaticLayout />,
        children: [
          { path: "/privacy", handle: { title: "Privacy policy" }, element: <PrivacyPage /> },
          { path: "/terms", handle: { title: "Terms of Use" }, element: <TermsPage /> },
          ...routesFor("public"),
        ],
      },
      { element: <HandoffLayout />, children: [{ path: "/error", element: <ErrorPage /> }, ...routesFor("handoff")] },
      ...devRoutes,
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppShell />,
            children: [...shellRedirects, ...routesFor("shell"), ...pendingShellRoutes()],
          },
          {
            path: "/workspaces/:workspaceId/settings",
            handle: { title: "Workspaces" },
            element: <FullPageSettingsLayout />,
          },
          ...routesFor("settings"),
        ],
      },
      {
        element: <KioskLayout />,
        children: [
          { path: "/kiosk/:kioskId", handle: { title: "Kiosk" }, element: <KioskPending /> },
          ...routesFor("kiosk"),
        ],
      },
      {
        element: <SharedReportLayout />,
        children: [
          { path: "/shared/:sharedReportId", handle: { title: "Reports" }, element: <SharedReportPending /> },
          ...routesFor("shared-report"),
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
